import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Avatar,
  Collapse,
  Slide,
  Grow
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CloseButton from '../Common/CloseButton';
import { tunableProviders, modelWithoutModelEditing } from './GlobalSettingUtils';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ModelDialog = ({
  open,
  onClose,
  editingModel,
  formData,
  onFormChange,
  onSave,
  getProviderOptionsForType,
  providerIcons,
  noTokenTextModels,
  resourceModels,
  isSaving,
  t
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const providerInfo = useMemo(() => providerIcons[formData.provider] || {}, [providerIcons, formData.provider]);
  const titleText = editingModel 
    ? t('global_setting.edit_model', 'Edit Model Configuration')
    : t('global_setting.add_model', 'Add Model Configuration');
  const providerLabelRaw = useMemo(() => getProviderOptionsForType(formData.model_type).find(opt => opt.value === formData.provider)?.label || '', [getProviderOptionsForType, formData.model_type, formData.provider]);
  const providerLabel = t(`global_setting.model_label_${formData.provider}`, providerLabelRaw || formData.provider);
  const existingTokenHint = editingModel && formData.has_api_token
    ? t('global_setting.api_token_saved_hint', 'Token stored securely. Leave blank to keep the current value.')
    : '';
  const existingSecretHint = editingModel && formData.has_api_secret
    ? t('global_setting.api_secret_saved_hint', 'Secret stored securely. Leave blank to keep the current value.')
    : '';
  const apiTokenRequired = !editingModel || !formData.has_api_token;
  const apiSecretRequired = !editingModel || !formData.has_api_secret;
  const apiTokenPlaceholder = formData.has_api_token && !formData.api_token ? '***' : formData.api_token;

  const renderLabel = (text, tooltipKey, fallback) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <span>{text}</span>
      <Tooltip title={tooltipKey ? t(tooltipKey, fallback) : fallback}>
        <IconButton size="small" sx={{ p: 0.25 }}>
          <InfoOutlined fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const supportsAdvancedTuning = useMemo(() => {
    return tunableProviders.has(formData.provider);
  }, [formData.provider]);

  const capability = useMemo(() => {
    const canTemperature = (() => {
      if (!supportsAdvancedTuning) return false;
      // OpenAI o1/o3 models disallow temperature
      if (formData.provider === 'openai') {
        const name = (formData.model_name || '').toLowerCase();
        if (name.startsWith('o1') || name.startsWith('o3')) return false;
      }
      return true;
    })();
    return {
      maxTokens: supportsAdvancedTuning,
      temperature: canTemperature,
      maxContext: true,
    };
  }, [formData.model_name, formData.provider, supportsAdvancedTuning]);

  const hasAdvancedFields = capability.maxContext || capability.maxTokens || capability.temperature;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
      keepMounted
    >
      <DialogTitle sx={{ p: 0 }}>
        <Grow in={open} timeout={250}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#5757b0', color: '#fff', p: 2, borderTopLeftRadius: '4px', borderTopRightRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={providerInfo?.icon} sx={{ width: 36, height: 36, bgcolor: 'transparent' }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {titleText}
                </Typography>
                {providerLabel && (
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {providerLabel}{formData.model_name ? ` · ${formData.model_name}` : ''}
                  </Typography>
                )}
              </Box>
            </Box>
            <CloseButton onClick={onClose} size="small" ariaLabel={t('global_setting.close', 'Close')} sx={{ color: '#fff' }} />
          </Box>
        </Grow>
      </DialogTitle>
      <DialogContent>
        <Grow in={open} timeout={300}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              select
              label={t('global_setting.provider', 'Provider')}
              value={formData.provider}
              onChange={onFormChange('provider')}
              fullWidth
              required
              SelectProps={{
                renderValue: (selected) => {
                  const option = getProviderOptionsForType(formData.model_type).find(opt => opt.value === selected);
                  const info = providerIcons[selected];
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={info?.icon}
                        sx={{ width: 24, height: 24, bgcolor: 'transparent' }}
                      />
                      <Typography variant="body2">{t(`global_setting.model_label_${selected}`, option?.label || selected)}</Typography>
                    </Box>
                  );
                }
              }}
            >
              {getProviderOptionsForType(formData.model_type).filter(opt => opt.value !== 'none').map((option) => {
                const info = providerIcons[option.value];
                return (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={info?.icon}
                        sx={{ width: 32, height: 32, bgcolor: 'transparent' }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="body1">{t(`global_setting.model_label_${option.value}`, option.label)}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: option?.accuracyColor || '#5757b0' }}>{option?.accuracy || ''}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          {!modelWithoutModelEditing.includes(formData.provider) && (
            <Grid item xs={12}>
              {['dataunfold', 'dataunfold_widget'].includes(formData.provider) ? (
                <TextField
                  select
                  label={t('global_setting.model_tier', 'Model Tier')}
                  value={formData.model_name || 'high'}
                  onChange={onFormChange('model_name')}
                  fullWidth
                  helperText={t(
                    'global_setting.dataunfold_tier_helper',
                    'Select processing tier: Low (fastest), Medium (balanced), High (best quality)',
                  )}
                >
                  <MenuItem value="low">
                    {t('global_setting.tier_low', 'Low')} - {t('global_setting.tier_low_desc', 'Fastest processing')}
                  </MenuItem>
                  <MenuItem value="medium">
                    {t('global_setting.tier_medium', 'Medium')} -{' '}
                    {t('global_setting.tier_medium_desc', 'Balanced performance')}
                  </MenuItem>
                  <MenuItem value="high">
                    {t('global_setting.tier_high', 'High')} - {t('global_setting.tier_high_desc', 'Best quality')}
                  </MenuItem>
                  <MenuItem value="low-coder">
                    {t('global_setting.tier_low_coder', 'Coder Low')} -{' '}
                    {t('global_setting.tier_low_coder_desc', 'Fast response + schema formatting')}
                  </MenuItem>
                  <MenuItem value="medium-coder">
                    {t('global_setting.tier_medium_coder', 'Coder Medium')} -{' '}
                    {t('global_setting.tier_medium_coder_desc', 'Balanced coder and conversational logic')}
                  </MenuItem>
                  <MenuItem value="high-coder">
                    {t('global_setting.tier_high_coder', 'Coder High')} -{' '}
                    {t('global_setting.tier_high_coder_desc', 'Highest quality code generation')}
                  </MenuItem>
                </TextField>
              ) : (
                <TextField
                  label={t('global_setting.model_name', 'Model Name')}
                  value={formData.model_name}
                  onChange={onFormChange('model_name')}
                  placeholder={t('global_setting.model_name_placeholder', 'e.g., gpt-4o, llama3.2')}
                  fullWidth
                  helperText={t('global_setting.model_name_helper', 'Leave empty for default model')}
                />
              )}
            </Grid>
          )}

          {!noTokenTextModels.includes(formData.provider) && (
            <Grid item xs={12}>
              <TextField
                label={t('global_setting.api_token', 'API Token')}
                value={apiTokenPlaceholder}
                onChange={onFormChange('api_token')}
                type="password"
                fullWidth
                required={apiTokenRequired}
              helperText={existingTokenHint}
            />
          </Grid>
        )}

        {hasAdvancedFields && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('global_setting.advanced_settings', 'Advanced')}
              </Typography>
              <Button size="small" onClick={() => setShowAdvanced(prev => !prev)}>
                {showAdvanced ? t('global_setting.hide', 'Hide') : t('global_setting.show', 'Show')}
              </Button>
            </Box>
            <Collapse in={showAdvanced} timeout="auto" unmountOnExit>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {capability.maxTokens && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={renderLabel(
                        t('global_setting.max_tokens', 'Max tokens'),
                        'global_setting.max_tokens_tip',
                        'Caps how many tokens the model can generate in its response (output limit); raise it for longer replies, lower it to control cost'
                      )}
                      value={formData.max_tokens}
                      onChange={onFormChange('max_tokens')}
                      type="number"
                      inputProps={{ min: 1 }}
                      fullWidth
                    />
                  </Grid>
                )}
                {capability.temperature && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={renderLabel(
                        t('global_setting.temperature', 'Temperature'),
                        'global_setting.temperature_tip',
                        'Controls randomness: 0 is deterministic, while higher values (up to 2) let the model explore more creative or varied outputs'
                      )}
                      value={formData.temperature}
                      onChange={onFormChange('temperature')}
                      type="number"
                      inputProps={{ step: 0.1, min: 0, max: 2 }}
                      fullWidth
                    />
                  </Grid>
                )}
                {capability.maxContext && (
                  <Grid item xs={12}>
                    <TextField
                      label={renderLabel(
                        t('global_setting.max_context_chars', 'Max context (characters)'),
                        'global_setting.max_context_helper',
                        'Defines how much recent conversation/text is kept when calling the LLM; a smaller number trims older context sooner to save tokens'
                      )}
                      value={formData.max_context_chars}
                      onChange={onFormChange('max_context_chars')}
                      type="number"
                      inputProps={{ min: 1 }}
                      fullWidth
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    label={renderLabel(
                      t('global_setting.timeout', 'Timeout (seconds)'),
                      'global_setting.timeout_helper',
                      'Maximum time to wait for AI response before timing out; increase for slower models or large images'
                    )}
                    value={formData.timeout}
                    onChange={onFormChange('timeout')}
                    type="number"
                    inputProps={{ min: 30, step: 30 }}
                    fullWidth
                    helperText={t('global_setting.timeout_default', 'Default: 360 seconds (6 minutes)')}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
          )}

          {resourceModels.includes(formData.provider) && (
            <>
              <Grid item xs={12}>
                <TextField
                  label={t('global_setting.api_endpoint', 'API Endpoint')}
                  value={formData.api_endpoint}
                  onChange={onFormChange('api_endpoint')}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('global_setting.resource_name', 'Resource Name')}
                  value={formData.resource_name}
                  onChange={onFormChange('resource_name')}
                  fullWidth
                />
              </Grid>
            </>
          )}

          {(formData.provider === 'aws_nova' || formData.provider === 'aws_claude') && (
            <>
              <Grid item xs={12}>
                <TextField
                  label={t('global_setting.api_secret', 'API Secret')}
                  value={formData.api_secret}
                  onChange={onFormChange('api_secret')}
                  type="password"
                  fullWidth
                  required={apiSecretRequired}
                  helperText={existingSecretHint}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('global_setting.api_region', 'API Region')}
                  value={formData.api_region}
                  onChange={onFormChange('api_region')}
                  fullWidth
                  required
                />
              </Grid>
            </>
          )}
        </Grid>
        </Grow>
      </DialogContent>
      <Grow in={open} timeout={300}>
        <DialogActions>
        <Button onClick={onClose}>
          {t('global_setting.cancel', 'Cancel')}
        </Button>
        <Button 
          onClick={onSave} 
          variant="contained"
          disabled={isSaving}
          sx={{ bgcolor: '#5757b0', '&:hover': { bgcolor: '#aaaaca' } }}
        >
          {isSaving ? t('global_setting.saving', 'Saving...') : t('global_setting.save', 'Save')}
        </Button>
        </DialogActions>
      </Grow>
    </Dialog>
  );
};

export default ModelDialog;
